import React, { useCallback, useState, useRef } from 'react';
// import '.msiDatagrid.scss';

import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import Swal from "sweetalert2";
import Popup from 'devextreme-react/popup';
import '@mdi/font/css/materialdesignicons.min.css';
import axios from 'axios';

import {
    DataGrid,
    DataGridRef,
    FilterRow,
    SearchPanel,
    GroupPanel,
    Grouping,
    DataGridTypes,
    Pager,
    Paging,
    Export,
    Selection,
    StateStoring,
    Editing,
    Form,
    Column
} from 'devextreme-react/data-grid';

const pageSizes = [10, 25, 50, 100];
const storageKey = "datagrid-state";

export interface ActionColumn {
    key: string;
    flex?: number;
    sortable?: boolean;
    minWidth?: number;
    field?: string;
    align?: 'left' | 'right' | 'center' | string;
    headerAlign?: 'left' | 'right' | 'center' | string;
    headerName?: string;
    caption?: string;
    renderCell: (params: { row: any; data?: any }) => React.ReactNode;
}

interface CustomRender {
    key: string;
    renderCell: (cellData: any) => React.ReactNode;
}

interface Props {
    array: any[];
    handleSelection?: any;
    rowClick?: any;
    cellClick?: any;
    toolbarPreparing?: any;
    customRender?: CustomRender[];
    hiddenColumns?: string[];
    actions?: ActionColumn[];
    actionsCaption?: string;
    groupPanel?: boolean;
}

// 🛠 helper format label kolom
const formatColumnLabel = (key: string): string => {
    if (key.includes("_")) {
        return key
            .split("_")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }
    return key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase());
};

export default function MsiDatagrid(props: Props) {
    const {
        array,
        handleSelection,
        rowClick,
        cellClick,
        toolbarPreparing,
        customRender = [],
        hiddenColumns = [],
        actions = [],
        groupPanel = false,
        actionsCaption
    } = props;

    const dataGrid = useRef<DataGridRef>(null);
    const [stateGrid, setStateGrid] = useState<any>(null);

    /** 📤 Export ke Excel */
    const onExporting = (e: DataGridTypes.ExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer: any) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
            });
        });
    };

    /** 📥 State storing load/save */
    const loadState = useCallback(() => {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        try {
            const stateLoad = JSON.parse(raw);
            setStateGrid(stateLoad);
            return stateLoad;
        } catch (err) {
            console.warn('Failed loading datagrid state', err);
            return null;
        }
    }, []);

    const saveState = useCallback((state: any) => {
        if (state) {
            (state.columns || []).forEach((col: any) => {
                if (col) col.filterValue = null;
            });
        }
        localStorage.setItem(storageKey, JSON.stringify(state));
    }, []);

    /** 📦 Save layout as file */
    const handleSaveFile = async () => {
        try {
            const jsonData = JSON.stringify(stateGrid);
            const blob = new Blob([jsonData], { type: "application/json" });
            saveAs(blob, "template.msitemplate");
            Swal.fire('Berhasil!', 'File Template telah disimpan.', 'success');
        } catch (error) {
            console.error(error);
            Swal.fire('Gagal!', 'Terjadi kesalahan saat menyimpan file.', 'error');
        }
    };

    /** 📡 Example API call */
    const handleAsTemplate = async () => {
        try {
            const response = await axios.post('/api/endpoint', { data: 'example' });
            console.log(response.data);
            Swal.fire('Berhasil!', 'Data dikirim sebagai template.', 'success');
        } catch (error) {
            console.error(error);
            Swal.fire('Gagal!', 'Terjadi kesalahan.', 'error');
        }
    };

    /** 🔧 Render kolom dinamis */
    const renderColumns = () => {
        if (array.length === 0) return null;

        // base columns dari data
        const baseColumns = Object.keys(array[0])
            .filter((key) => !hiddenColumns.includes(key))
            .map((key) => {
                const custom = customRender.find((c) => c.key === key);
                return (
                    <Column
                        key={key}
                        dataField={key}
                        caption={formatColumnLabel(key)}
                        cellRender={
                            custom
                                ? (cellData) => custom.renderCell(cellData)
                                : (cellData) => cellData.text
                        }
                    />
                );
            });

        // extra columns yg cuma ada di customRender
        const extraColumns = customRender
            .filter((col) => !Object.keys(array[0]).includes(col.key) && !hiddenColumns.includes(col.key))
            .map((col) => (
                <Column
                    key={col.key}
                    dataField={col.key}
                    caption={formatColumnLabel(col.key)}
                    cellRender={col.renderCell}
                />
            ));

        // kolom actions
        const actionColumns = actions.map((act) => (
            <Column
                key={act.key}
                dataField={act.field ?? act.key}
                caption={act.headerName ?? act.caption ?? actionsCaption ?? "ACTIONS"}
                alignment={act.align as any}
                minWidth={act.minWidth}
                allowSorting={act.sortable ?? false}
                headerCellRender={
                    act.headerAlign
                        ? () => (
                            <div style={{ textAlign: act.headerAlign as any }}>
                                {act.headerName ?? act.caption}
                            </div>
                        )
                        : undefined
                }
                cellRender={({ data }) => act.renderCell({ row: data, data })}
                fixed
                fixedPosition="left"
            />
        ));

        return [...baseColumns, ...extraColumns, ...actionColumns];
    };

    return (
        <DataGrid
            repaintChangesOnly
            ref={dataGrid}
            dataSource={array}
            allowColumnReordering
            columnAutoWidth
            rowAlternationEnabled
            showBorders
            showColumnHeaders={array.length > 0}  // ✅ header hilang kalau kosong
            onExporting={onExporting}
            onSelectionChanged={handleSelection}
            onRowClick={rowClick}
            onCellClick={cellClick}
            width="100%"
            key={999}
            onToolbarPreparing={toolbarPreparing}
            keyExpr="id"
        >
            <FilterRow visible={false} />
            <GroupPanel visible={groupPanel} />
            <Grouping autoExpandAll={false} texts={{ groupByThisColumn: "" }} />
            <SearchPanel visible highlightCaseSensitive />
            <Pager visible allowedPageSizes={pageSizes} showPageSizeSelector />
            <Paging defaultPageSize={10} />
            <Export enabled allowExportSelectedData />
            <Editing mode="popup" allowUpdating={false} allowAdding={false} allowDeleting={false} useIcons>
                <Popup title="Edit Data" showTitle width={500} height={300} />
                <Form colCount={2} labelLocation="top" />
            </Editing>
            <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" />
            {/* <StateStoring
                enabled={false}
                type="custom"
                storageKey={storageKey}
                customLoad={loadState}
                customSave={saveState}
            /> */}

            {renderColumns()}
        </DataGrid>
    );
}
